import moment from "moment";
import { getWeekDaysFromDateRange } from "@/utils/dates";
import { cutString } from "@/utils/stringFormat";
import Loader from "@/components/loader/Loader.vue";
import { FacilitySettingsModel } from "@/models/FacilitySettings";

export default {
  name: "LifestyleEventsReport",
  data() {
    return {
      period: "month",
      startDate: moment().subtract(1, "month").toISOString().substring(0, 10),
      endDate: moment().toISOString().substring(0, 10),
      timeFormat: null,
      flexHeight: 250,
      loading: false,
      picker: {
        start: false,
        end: false
      },
      periods: [],
      headers: [],
      jsonFields: {
        "Event": "eventName",
        "Lifestyle program": "socialClubsNames",
        "Dimension": "eventCategories",
        "Resident": "residentName",
        "Location": "location",
        "Recurring event": "recurringEvent",
        "Date": "eventStart"
      },

      eventsByHour: [],
      eventsByDate: [],
      dailyCount: [],
    }
  },
  components: {
    Loader
  },
  computed: {
    facility(): FacilitySettingsModel {
      return this.$store.state.facilityModule.selectedFacility;
    },
    attendanceData(): any {
      if (!this.report?.lifestyleEvents) return { attended: 0, notAttended: 0, total: 0 };
      
      // Console logging for lifestyle events inspection
      console.group('Lifestyle Events Report Data')
      console.log('Raw lifestyle events:', this.report.lifestyleEvents)
      
      let attendedEvents = 0
      let notAttendedEvents = 0
      
      const totalEvents = this.report.lifestyleEvents.length
      
      this.report.lifestyleEvents.forEach(event => {
        const actualAttendees = event.attendeesIds ? event.attendeesIds.length : 0
        
        
        // Count ALL events - if it has any attendees, it's attended, otherwise not attended
        if (actualAttendees > 0) {
          attendedEvents++
        } else {
          notAttendedEvents++
        }
      })
      
      const attendanceData = {
        attended: attendedEvents,
        notAttended: notAttendedEvents,
        total: totalEvents,
        attendedPercent: totalEvents > 0 ? Math.round((attendedEvents / totalEvents) * 100) : 0,
        notAttendedPercent: totalEvents > 0 ? Math.round((notAttendedEvents / totalEvents) * 100) : 0
      }
      
      console.log('Attendance summary:', attendanceData)
      console.groupEnd()
      
      return attendanceData
    },
    chartMessages(): any {
      return { empty: this.$t('GeneralTranslations.NoData') };
    },
    
    report() {
      this.loading = false
      return this.$store.state.calenderModule.lifestyleEventsReportData;
    },
    formattedSelectedDateRange() {
      return `${moment(this.startDate).format(this.facility.dateFormat)} - ${moment(this.endDate).format(this.facility.dateFormat)}`
    },
    formattedStartDate() {
      return moment(this.startDate).format(this.facility.dateFormat)
    },
    formattedEndDate() {
      return moment(this.endDate).format(this.facility.dateFormat)
    },
    sortedResidents(): any[] {
      if (!this.report?.residents) return []
      
      const residents = [...this.report.residents]
      
      // Sort by highest engagement (lifestyle events count)
      return residents.sort((a, b) => (b.lifestyleEventsNumber || b.eventsCount || 0) - (a.lifestyleEventsNumber || a.eventsCount || 0))
    }
  },
 
  async created() {
    if(this.$route.params.facilityId && (this.$store.state.authModule.currentFacility !== this.$route.params.facilityId)){
      this.loading = true
      await this.changeCurrentFacility(this.$route.params.facilityId)
      this.loading = false
    }

    this.getEventsByDates()
    this.timeFormat = this.facility.timeFormat.includes('A')?'hh  A':'HH'
    this.headers = [
      { text: this.$i18n.messages[this.$i18n.locale]["LifestyleEventsReport.Event"], value: "eventName" },
      { text: this.$i18n.messages[this.$i18n.locale]["LifestyleEventsReport.LifestyleProgram"], value: "socialClubsNames" },
      { text: this.$i18n.messages[this.$i18n.locale]["LifestyleEventsReport.Dimension"], value: "eventCategories" },
      { text: this.$i18n.messages[this.$i18n.locale]["LifestyleEventsReport.Resident"], value: "residentName" },
      { text: this.$i18n.messages[this.$i18n.locale]["LifestyleEventsReport.Location"], value: "location" },
      { text: this.$i18n.messages[this.$i18n.locale]["LifestyleEventsReport.RecurringEvent"], value: "isRecurring" },
      { 
        text: this.$i18n.messages[this.$i18n.locale]["LifestyleEventsReport.Date"], 
        value: "eventStart",
        sort: (a, b) => {
          // Parse dates and sort chronologically
          const dateA = moment(a, "DD MMM, HH:mm")
          const dateB = moment(b, "DD MMM, HH:mm")
          return dateA.valueOf() - dateB.valueOf()
        }
      }
    ]
    this.periods = [
      { title: this.$i18n.messages[this.$i18n.locale]["GeneralTranslations.Week"], value: "week" },
      { title: this.$i18n.messages[this.$i18n.locale]["GeneralTranslations.Month"], value: "month" },
      { title: this.$i18n.messages[this.$i18n.locale]["GeneralTranslations.Year"], value: "year" }
    ]
  },
  watch: {
    "report"(report) {
      this.eventsByDate = this.groupByDay(report.lifestyleEvents)
      this.dailyCount = this.getDailyCount(report.lifestyleEvents)
      this.eventsByHour = this.groupByHour(report.lifestyleEvents)
    }
  },
  methods: {
    async changeCurrentFacility(id) {
      this.$store.dispatch('setIsChangingFacilityOnCurrentBrowser', true);
      await this.$store.dispatch('changeCurrentUserFacility', { newFacilityId: id, redirectEnabled: false })
    },
    routeToReports() {
      this.$router.push("/reports");
    },
    routeToLifestyleResidentsReport() {
      this.$router.push("/reports/residentsLifestyleEventsReport")
    },
    getEventsByDates() {
      this.loading = true
      this.$store.dispatch("getLifestyleEventsReportData", {
        startDate: moment(this.startDate).startOf('day').toDate(),
        endDate: moment(this.endDate).endOf('day').toDate()
      });
    },
    getDailyCount(events) {
      const types = events.reduce(function (totals, event) {
        const date = moment(event.start)
          .startOf("day")
          .format("MMM  D");
        !totals[date] ? (totals[date] = 1) : (totals[date] = totals[date] + 1);
        return totals;
      }, {});
      return types;
    },
    groupByDay(events) {
      const weekDays = getWeekDaysFromDateRange(moment(this.startDate), moment(this.endDate))
      const week = weekDays.reduce((o, key) => ({ ...o, [key]: undefined }), {})
      const types = events.reduce(function (totals, event) {
        const dayOfWeek = moment(event.start).format("dddd");
        !week[dayOfWeek] ? (week[dayOfWeek] = 1) : (week[dayOfWeek] = totals[dayOfWeek] + 1);
        return week;
      }, {});
      return types;
    },
    groupByHour(events) {
      const timeFormat = this.timeFormat
      const types = events.reduce(function (totals, event) {
        const dayOfWeek = moment(event.start).format(timeFormat);
        !totals[dayOfWeek]
          ? (totals[dayOfWeek] = 1)
          : (totals[dayOfWeek] = totals[dayOfWeek] + 1);
        return totals;
      }, {});
      return types;
    },
    handleChangePeriod(period) {
      this.startDate = moment()
        .subtract(1, period)
        .toISOString()
        .substring(0, 10);

      this.endDate = moment()
        .toISOString()
        .substring(0, 10);

      //reload report
      this.getEventsByDates()
    },
    truncateResidentName(name) {
      return cutString(name, 16);
    }
  }
}
